import React from 'react';
import { motion } from 'framer-motion';
import { CrosshairIcon, RadioIcon, ActivityIcon } from 'lucide-react';
export function CoreModules() {
  return <section id="modules" className="relative w-full py-24 md:py-32 px-6 md:px-12 bg-[#050508]" data-id="element-26">
      {/* Section header */}
      <div className="max-w-4xl mx-auto text-center mb-16 md:mb-20" data-id="element-27">
        <motion.h2 className="font-['Bebas_Neue'] text-5xl md:text-7xl tracking-wider text-[#C4C4CC] mb-4" initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.7
      }} data-id="element-28">
          CORE MODULES
        </motion.h2>
        <motion.div className="w-24 h-[1px] bg-[#FF2D2D] mx-auto mb-6" initial={{
        scaleX: 0
      }} whileInView={{
        scaleX: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        delay: 0.3
      }} data-id="element-29" />
      </div>

      {/* Module cards */}
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8" data-id="element-30">
        {/* Phantom Matching */}
        <motion.div className="border border-[#8A8A96]/20 bg-[#0A0A0F] p-8 flex flex-col relative overflow-hidden group" initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.6,
        delay: 0
      }} data-id="element-31">
          <div className="absolute inset-0 border border-[#FF2D2D]/0 group-hover:border-[#FF2D2D]/30 transition-colors duration-500 pointer-events-none" data-id="element-32" />
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{
          boxShadow: 'inset 0 0 60px rgba(255, 45, 45, 0.05)'
        }} data-id="element-33" />

          <div className="w-12 h-12 border border-[#8A8A96]/30 flex items-center justify-center mb-6" data-id="element-34">
            <CrosshairIcon className="w-6 h-6 text-[#FF2D2D]" data-id="element-35" />
          </div>

          <span className="font-['Bebas_Neue'] text-xs tracking-[0.3em] text-[#8A8A96] mb-2" data-id="element-36">
            MODULE 01
          </span>
          <h3 className="font-['Bebas_Neue'] text-2xl md:text-3xl text-[#FF2D2D] tracking-wider mb-4" data-id="element-37">
            Phantom Matching
          </h3>
          <p className="text-[#8A8A96] text-sm leading-relaxed font-light flex-1" data-id="element-38">
            Unseen pattern recognition across behavioral micro-signals. Matches
            players to games before explicit intent forms.
          </p>

          {/* Bottom circuit accent */}
          <svg className="mt-6 w-full h-4" viewBox="0 0 300 16" fill="none" data-id="element-39">
            <path d="M0 8 H100 V2 H200 V14 H300" stroke="#FF2D2D" strokeWidth="0.5" opacity="0.3" data-id="element-40" />
            <circle cx="100" cy="8" r="2" fill="#FF2D2D" opacity="0.3" data-id="element-41" />
            <circle cx="200" cy="2" r="2" fill="#FF2D2D" opacity="0.3" data-id="element-42" />
          </svg>
        </motion.div>

        {/* Relay Distribution */}
        <motion.div className="border border-[#8A8A96]/20 bg-[#0A0A0F] p-8 flex flex-col relative overflow-hidden group" initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.6,
        delay: 0.15
      }} data-id="element-43">
          <div className="absolute inset-0 border border-[#FF2D2D]/0 group-hover:border-[#FF2D2D]/30 transition-colors duration-500 pointer-events-none" data-id="element-44" />
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{
          boxShadow: 'inset 0 0 60px rgba(255, 45, 45, 0.05)'
        }} data-id="element-45" />

          <div className="w-12 h-12 border border-[#8A8A96]/30 flex items-center justify-center mb-6" data-id="element-46">
            <RadioIcon className="w-6 h-6 text-[#FF2D2D]" data-id="element-47" />
          </div>

          <span className="font-['Bebas_Neue'] text-xs tracking-[0.3em] text-[#8A8A96] mb-2" data-id="element-48">
            MODULE 02
          </span>
          <h3 className="font-['Bebas_Neue'] text-2xl md:text-3xl text-[#FF2D2D] tracking-wider mb-4" data-id="element-49">
            Relay Distribution
          </h3>
          <p className="text-[#8A8A96] text-sm leading-relaxed font-light flex-1" data-id="element-50">
            Instant multi-operator syndication. One integration, continental
            reach. Games deployed across 340+ operator lobbies simultaneously.
          </p>

          <svg className="mt-6 w-full h-4" viewBox="0 0 300 16" fill="none" data-id="element-51">
            <path d="M0 14 H80 V8 H150 V2 H220 V8 H300" stroke="#FF2D2D" strokeWidth="0.5" opacity="0.3" data-id="element-52" />
            <circle cx="80" cy="14" r="2" fill="#FF2D2D" opacity="0.3" data-id="element-53" />
            <circle cx="150" cy="8" r="2" fill="#FF2D2D" opacity="0.3" data-id="element-54" />
            <circle cx="220" cy="2" r="2" fill="#FF2D2D" opacity="0.3" data-id="element-55" />
          </svg>
        </motion.div>

        {/* Trace Analytics */}
        <motion.div className="border border-[#8A8A96]/20 bg-[#0A0A0F] p-8 flex flex-col relative overflow-hidden group" initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.6,
        delay: 0.3
      }} data-id="element-56">
          <div className="absolute inset-0 border border-[#FF2D2D]/0 group-hover:border-[#FF2D2D]/30 transition-colors duration-500 pointer-events-none" data-id="element-57" />
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{
          boxShadow: 'inset 0 0 60px rgba(255, 45, 45, 0.05)'
        }} data-id="element-58" />

          <div className="w-12 h-12 border border-[#8A8A96]/30 flex items-center justify-center mb-6" data-id="element-59">
            <ActivityIcon className="w-6 h-6 text-[#FF2D2D]" data-id="element-60" />
          </div>

          <span className="font-['Bebas_Neue'] text-xs tracking-[0.3em] text-[#8A8A96] mb-2" data-id="element-61">
            MODULE 03
          </span>
          <h3 className="font-['Bebas_Neue'] text-2xl md:text-3xl text-[#FF2D2D] tracking-wider mb-4" data-id="element-62">
            Trace Analytics
          </h3>
          <p className="text-[#8A8A96] text-sm leading-relaxed font-light flex-1" data-id="element-63">
            Full-funnel visibility from impression to revenue. Every touchpoint
            measured, every conversion attributed.
          </p>

          <svg className="mt-6 w-full h-4" viewBox="0 0 300 16" fill="none" data-id="element-64">
            <path d="M0 2 H120 V14 H180 V8 H300" stroke="#FF2D2D" strokeWidth="0.5" opacity="0.3" data-id="element-65" />
            <circle cx="120" cy="2" r="2" fill="#FF2D2D" opacity="0.3" data-id="element-66" />
            <circle cx="180" cy="14" r="2" fill="#FF2D2D" opacity="0.3" data-id="element-67" />
          </svg>
        </motion.div>
      </div>
    </section>;
}